import { customerHelper } from '@tweddle-se-team/common/constants'
import { i18nHelper } from '@tweddle-se-team/common/helpers'
import React, { useMemo, useEffect, useCallback } from 'react'
import { Nav, Dropdown } from 'rsuite'
import { FaUserCircle } from 'react-icons/fa'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import { configSelectors, configsActions } from '@/store/configs'
import { i18nL10nSelectors, i18nL10nActions } from '@/store/i18nL10n'
import { loggedInUserActions, loggedInUserSelector } from '@/store/loggedInUser'
import { productActions } from '@/store/products'
import { searchActions } from '@/store/search'
import { publicationSelectors } from '@/store/publication'
import { overviewSelectors } from '@/store/overview'
import { headerSelectors } from '@/store/header'
import NavigationItems from './navigationItems'
import navigationOptions from './headerHelpers'
import { CustomNavIcoItem, CustomDropdown } from '../themes/tme/header/headerElements'
import { proxyInstance } from '@/hooks/useApi'
import SubaruHeader from '@/components/themes/subaru/header'
import TMEHeader from '@/components/themes/tme/header'
import qs from 'qs'
import { showWarning } from '@/components/shared/toaster'
import useTranslation, { useTranslateInLocale } from '@/hooks/useTranslation'
import CustomerNotFound from '@/components/shared/customerNotFound'
import { findCachedPublicationInLanguage } from '@/utils/indexedDBHelper'

const { setLocale } = i18nHelper
const { CUSTOMER_IDS } = customerHelper

const headerMapper = {
  [CUSTOMER_IDS.SUBARU]: SubaruHeader,
  [CUSTOMER_IDS.TME]: TMEHeader,
  [CUSTOMER_IDS.NORDICS]: SubaruHeader
}

const canShowLoginByCustomer = {
  [CUSTOMER_IDS.TME]: ({ showStaticLogo }) => !showStaticLogo,
  [CUSTOMER_IDS.SUBARU]: ({ showStaticLogo }) => !showStaticLogo,
  [CUSTOMER_IDS.NORDICS]: () => true
}

const AuthenticatedMenuOptions = ({ logOut = () => {}, customerId }) => {
  const router = useRouter()
  const t = useTranslation()
  const loggedInUser = useSelector(loggedInUserSelector.selectUser)
  const signedInAsAdmin = useSelector(loggedInUserSelector.selectHasRole('Admin'))
  const signedIn = loggedInUser && loggedInUser._id
  const { isOnline, showStaticLogo } = useSelector(configSelectors.selectConfigs)
  const login = useCallback(() => router.push('/login'), [router])
  const canShowLogin = canShowLoginByCustomer[customerId] || canShowLoginByCustomer[CUSTOMER_IDS.TME]

  return (
    <>
      {signedIn && isOnline && (
        <CustomDropdown
          icon={<FaUserCircle aria-hidden="true" size={24} />}
          placement="bottomEnd"
          eventKey={7}
          noCaret
          id="user_profile"
          aria-label={t('header_user_menu', 'User menu')}
        >
          <Nav.Item>
            <p>Signed in as: </p>
            <strong>{loggedInUser.userName}</strong>
          </Nav.Item>
          <Dropdown.Separator />
          {signedInAsAdmin && (
            <NavigationItems
              router={router}
              items={navigationOptions.admin.filter(item => !item.customerIds || item.customerIds.includes(customerId))}
            />
          )}

          <Nav.Item onClick={logOut}>Log out</Nav.Item>
        </CustomDropdown>
      )}
      {!signedIn && isOnline && canShowLogin({ showStaticLogo }) && (
        <CustomNavIcoItem onClick={login} id="user_profile" aria-label={t('header_login', 'Log in')}>
          <FaUserCircle aria-hidden="true" size={24} />
        </CustomNavIcoItem>
      )}
    </>
  )
}

const Header = () => {
  const router = useRouter()
  const dispatch = useDispatch()
  const { customerId, isOnline } = useSelector(configSelectors.selectConfigs)
  const locale = useSelector(i18nL10nSelectors.selectLocale)
  const { relatedPublications, content } = useSelector(publicationSelectors.selectPublication)
  const overviewOptions = useSelector(overviewSelectors.selectOptions)
  const { image: _, ...selectedModel } = useSelector(headerSelectors.selectModel)
  const tLoc = useTranslateInLocale()

  const changeLocaleOffline = useCallback(
    async locale => {
      setLocale(locale)
      dispatch(i18nL10nActions.SET_LOCALE(locale))

      if (router.pathname === '/content') {
        const id = router.query.id || new URLSearchParams(window.location.search).get('id')
        try {
          const cachedPublication = await findCachedPublicationInLanguage(id, locale)
          if (cachedPublication) {
            const queryParams = {
              id: cachedPublication.ditaId,
              language: locale,
              publicationType: cachedPublication.publicationFromApi.publicationType
            }
            // Reload the cached page: client-side navigation keeps the viewer's previous publication state.
            // eslint-disable-next-line @next/next/no-location-assign-relative-destination
            window.location.href = `/content?${qs.stringify(queryParams)}`
            return
          }
        } catch (error) {
          console.error('Failed to find cached publication for language:', error)
        }
      }

      router.push({ pathname: '/cachedPublications', query: { language: locale } }, undefined, {
        shallow: router.pathname === '/cachedPublications'
      })
    },
    [dispatch, router]
  )

  const changeLocaleTME = useCallback(
    async locale => {
      setLocale(locale)
      dispatch(i18nL10nActions.SET_LOCALE(locale))
      router.push('/')
    },
    [dispatch, router]
  )

  const changeLocaleSubaru = useCallback(
    async locale => {
      setLocale(locale)
      dispatch(i18nL10nActions.SET_LOCALE(locale))
      const { pathname, query } = router

      if (pathname === '/content') {
        if (relatedPublications && relatedPublications[locale]) {
          const queryParams = {
            id: relatedPublications[locale],
            language: locale,
            publicationType: query.publicationType,
            ...(content?.resourceKey && { topicId: content.resourceKey })
          }
          // A full reload resets publication state when switching to its related translation.
          // eslint-disable-next-line @next/next/no-location-assign-relative-destination
          window.location.href = `/content?${qs.stringify(queryParams)}`
        } else {
          // Validate that all required fields are present in selectedModel
          const requiredFields = ['brand', 'model', 'modelType', 'year']
          const hasAllRequiredFields = requiredFields.every(field => selectedModel?.[field])

          if (hasAllRequiredFields) {
            const queryParams = { ...(overviewOptions || {}), ...(selectedModel || {}), language: locale }
            showWarning(tLoc('publication_change_language_redirect', locale))
            router.push(`/publications?${qs.stringify(queryParams)}`, undefined, { shallow: false })
          } else {
            showWarning(tLoc('publication_change_language_redirect_landing', locale))
            router.push('/', undefined, { shallow: false })
          }
        }
        return
      }

      const newQuery = { ...query, language: locale }
      const url = { pathname, query: newQuery }
      router.push(url, undefined, { shallow: pathname === '/cachedPublications' })
    },
    [dispatch, router, relatedPublications, content, overviewOptions, selectedModel, tLoc]
  )

  const changeLocaleMapper = useMemo(
    () => ({
      [CUSTOMER_IDS.TME]: changeLocaleTME,
      [CUSTOMER_IDS.SUBARU]: changeLocaleSubaru,
      [CUSTOMER_IDS.NORDICS]: changeLocaleSubaru
    }),
    [changeLocaleTME, changeLocaleSubaru]
  )

  const { logOut, onBrandChange, setIsIframe } = useMemo(() => getStoreActions(dispatch), [dispatch])

  const returnToLandingPage = useCallback(
    (e, isOnline) => {
      e.preventDefault()
      isOnline
        ? router.push('/', undefined, { shallow: false })
        : router.push('/cachedPublications', undefined, { shallow: false })
    },
    [router]
  )

  useEffect(() => setIsIframe(window.self !== window.top))

  const BrandedHeader = headerMapper[customerId] || CustomerNotFound

  const effectiveChangeLocale = isOnline ? changeLocaleMapper[customerId] || changeLocaleTME : changeLocaleOffline
  return (
    <BrandedHeader
      onBrandChange={onBrandChange}
      returnToLandingPage={returnToLandingPage}
      changeLocale={effectiveChangeLocale}
      autentincatedOptions={<AuthenticatedMenuOptions logOut={logOut} customerId={customerId} />}
      locale={locale}
      isOnline={isOnline}
      customerId={customerId}
    />
  )
}

const getStoreActions = dispatch => ({
  logOut: async () => {
    await proxyInstance.post('auth/logout')
    dispatch(loggedInUserActions.RESET())
    window.location.replace('/login')
  },
  onBrandChange: (brand, callback = () => {}) => {
    dispatch(productActions.SET_BRAND(brand))
    dispatch(searchActions.SET_BRAND(brand))
    callback()
  },
  setIsIframe: isIframe => {
    dispatch(configsActions.SET_IS_IN_IFRAME(isIframe))
  }
})

export default Header
